import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authApi, emisoresApi } from '../utils/api';
import { ponerSesionEmbebida } from '../utils/sesionEmbebida';

/**
 * Embebido en otro producto (Unity 360), este store NO persiste.
 *
 * El iframe corre en el mismo origen que la aplicación normal, así que persistir
 * acá **le cambiaría la sesión al usuario en sus otras pestañas**: quien tuviera
 * Finance abierto aparte se encontraría de pronto con la empresa que Unity
 * abrió y un token de 8 horas, sin haber hecho nada, y se quedaría con eso al
 * cerrar el iframe.
 *
 * Con un almacén en memoria el store se comporta igual —las pantallas siguen
 * leyendo `currentEmisor` sin saber que están embebidas— pero no lee ni escribe
 * nada compartido. La otra mitad, el token de las llamadas, está en
 * `utils/sesionEmbebida.js`.
 *
 * Se decide al cargar el módulo porque la dirección ya lo dice, y porque el
 * almacén de `persist` no se puede cambiar después.
 */
const esPantallaEmbebida = typeof window !== 'undefined' &&
  window.location.pathname.startsWith('/embed');

const enMemoria = (() => {
  const datos = new Map();
  return {
    getItem: (k) => (datos.has(k) ? datos.get(k) : null),
    setItem: (k, v) => { datos.set(k, v); },
    removeItem: (k) => { datos.delete(k); }
  };
})();

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      emisores: [],
      currentEmisor: null,
      isLoading: false,
      error: null,

      // Page status (for header display)
      pageStatus: null, // { text: 'Facturando...', subtext: '₡ Colones • Contado', gradient: 'blue' }

      // Exchange rate (fetched once on login)
      tipoCambio: null,
      tipoCambioFecha: null,

      // Actions
      login: async (email, password, tenantSlug) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login(email, password, tenantSlug);
          const { user, emisores, token } = response.data.data;

          // Store token and tenant slug in localStorage for API interceptor
          localStorage.setItem('token', token);
          localStorage.setItem('tenantSlug', tenantSlug);

          // Find default emisor or use first one
          const defaultEmisor = emisores.find(e => e.isDefault) || emisores[0];
          if (defaultEmisor) {
            localStorage.setItem('emisorId', defaultEmisor.id);
          }

          set({
            user,
            token,
            emisores,
            currentEmisor: defaultEmisor || null,
            isLoading: false
          });

          return { success: true };
        } catch (error) {
          const message = error.response?.data?.error || 'Login failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('tenantSlug');
        localStorage.removeItem('emisorId');
        set({
          user: null,
          token: null,
          emisores: [],
          currentEmisor: null,
          error: null
        });
      },

      setCurrentEmisor: async (emisor) => {
        localStorage.setItem('emisorId', emisor.id);
        set({ currentEmisor: emisor });

        // Update default on server
        try {
          await emisoresApi.setDefault(emisor.id);
        } catch (error) {
          if (error?.code !== 'ERR_CANCELED') console.error('Failed to update default emisor:', error);
        }
      },

      setEmisores: (emisores) => {
        set({ emisores });
      },

      setPageStatus: (status) => {
        set({ pageStatus: status });
      },

      clearPageStatus: () => {
        set({ pageStatus: null });
      },

      setTipoCambio: (rate) => {
        set({
          tipoCambio: rate,
          tipoCambioFecha: new Date().toISOString()
        });
      },

      fetchTipoCambio: async () => {
        // Leer SIEMPRE de la tabla tipos_cambio del tenant (sincronizada con BCCR
        // cada día por syncExchangeRate cron). Antes usábamos /exchange-rate global
        // que pega un wrapper externo (exchangecr.onrender.com) con cache de 1h por
        // worker PM2 — cuando el wrapper devolvía venta:null cada uno de los 4
        // workers servía un cache stale distinto y las FEs salían con TCs random
        // (vimos 458.68, 460.18 cuando el real era 455.90).
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const hoyCR = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Costa_Rica' }); // YYYY-MM-DD
        try {
          // Endpoint tenant-aware lee de BD (tabla tipos_cambio).
          // Usa el axios api con headers de tenant ya configurados.
          const { default: api } = await import('../utils/api');
          const response = await api.get(`/exchange-rate/${hoyCR}`);
          const data = response.data;
          if (data?.success && data?.venta) {
            set({
              tipoCambio: data.venta,
              tipoCambioFecha: new Date().toISOString()
            });
            return data.venta;
          }
        } catch (error) {
          console.error('Error fetching exchange rate from DB:', error.message);
        }
        // Fallback al endpoint global (wrapper externo) si la BD no respondió
        try {
          const response = await fetch(`${apiUrl}/exchange-rate`);
          const data = await response.json();
          if (data?.success && data?.venta) {
            set({
              tipoCambio: data.venta,
              tipoCambioFecha: new Date().toISOString()
            });
            return data.venta;
          }
        } catch (error) {
          console.error('Error fetching exchange rate fallback:', error);
        }
        return null;
      },

      refreshUser: async () => {
        try {
          const response = await authApi.me();
          const { user, emisores } = response.data.data;

          // Find current or default emisor
          const currentId = localStorage.getItem('emisorId');
          const currentEmisor = emisores.find(e => e.id === parseInt(currentId)) ||
            emisores.find(e => e.isDefault) ||
            emisores[0];

          if (currentEmisor) {
            localStorage.setItem('emisorId', currentEmisor.id);
          }

          set({
            user,
            emisores,
            currentEmisor
          });
        } catch (error) {
          // If refresh fails, clear auth state
          get().logout();
        }
      },

      // Check if user is authenticated
      isAuthenticated: () => {
        const { token } = get();
        return !!token;
      },

      // Sesión de soporte abierta desde la consola SuperAdmin (token temporal del admin)
      impersonar: async (token, tenantSlug) => {
        get().logout();
        localStorage.setItem('token', token);
        localStorage.setItem('tenantSlug', tenantSlug);
        set({ token });
        await get().refreshUser();
        return !!get().user;
      },

      // Set auth data after registration (called from Register page)
      setAuthData: (data) => {
        const { user, token, emisor, tenantSlug } = data;

        // Store in localStorage for API interceptor
        localStorage.setItem('token', token);
        localStorage.setItem('tenantSlug', tenantSlug);
        localStorage.setItem('emisorId', emisor.id);

        // Update zustand store
        set({
          user,
          token,
          emisores: [emisor],
          currentEmisor: emisor,
          isLoading: false
        });
      },

      /**
       * Sesión abierta desde otro producto por canje de boleto (Unity 360).
       *
       * No es `setAuthData` con otro nombre: esa escribe en `localStorage`, y acá
       * eso es justo lo que no se puede hacer —le cambiaría la sesión al usuario
       * en sus otras pestañas—. El token va al módulo en memoria que lee el
       * interceptor, y el store no persiste nada (ver arriba).
       */
      /**
       * La sesión que llega desde Unity 360 por el menú de portales (ítem 058).
       *
       * ── POR QUE NO ES `abrirSesionEmbebida`, QUE ESTA JUSTO ABAJO ─────────
       *
       * La embebida vive EN MEMORIA a propósito: es una pantalla de Finance
       * dentro de un iframe de Unity, y su sesión no debe sobrevivir a la
       * pestaña ni cambiarle la sesión a quien tenga Finance abierto al lado.
       *
       * Esta es lo contrario: la persona viene a **trabajar en Finance**, en su
       * propia pestaña. Con la sesión en memoria, recargar la perdería y la
       * mandaría al login — justo lo que el salto venía a evitar. Así que se
       * guarda como la del login normal, con las tres claves que el interceptor
       * de la API lee en cada llamada.
       *
       * ── Y POR QUE NO SE REUSA `login` ─────────────────────────────────────
       *
       * Porque `login` pide correo y contraseña. Acá la sesión ya la emitió el
       * servidor a cambio de una identidad firmada por Unity: no hay clave que
       * pedir, y fabricar una para pasar por esa puerta sería mentirle al store.
       */
      abrirSesionDesdeUnity: ({ user, token, emisores, tenantSlug, emisorId }) => {
        localStorage.setItem('token', token);
        if (tenantSlug) localStorage.setItem('tenantSlug', tenantSlug);
        // `/suite/entrar` no impone empresa —la persona entra a Finance entero—
        // así que se toma la predeterminada, igual que el login normal.
        const empresa = (emisorId && emisores?.find(e => String(e.id) === String(emisorId))) ||
          emisores?.find(e => e.isDefault) || emisores?.[0] || null;
        if (empresa) localStorage.setItem('emisorId', empresa.id);
        set({
          user,
          token,
          emisores: emisores || [],
          currentEmisor: empresa,
          isLoading: false,
          error: null
        });
      },

      abrirSesionEmbebida: ({ user, token, emisores, tenantSlug, emisorId }) => {
        ponerSesionEmbebida({ token, tenantSlug, emisorId });
        const empresa = emisores?.find(e => String(e.id) === String(emisorId)) || emisores?.[0] || null;
        set({
          user,
          token,
          emisores: emisores || [],
          currentEmisor: empresa,
          isLoading: false,
          error: null
        });
      }
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => (esPantallaEmbebida ? enMemoria : localStorage)),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        emisores: state.emisores,
        currentEmisor: state.currentEmisor
      })
    }
  )
);

export default useAuthStore;
